import styled from '@emotion/styled';
import React, {createRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import loading from '@ELib/assets/images/loading.svg';
import {mixinFlexCenter} from '@ELib/styles/mixins';
import {useMountEffect} from 'webapp-lib/utils';

type ELoadingProps = {
  className?: string;
  noDelay?: boolean;
};

/**
 * Beware this ELoading component does not display the image correctly in development modus
 * and should preferably be avoided. The `Loading` component in the webapp works properly.
 */
export const ELoading = ({
  className,
  noDelay,
}: ELoadingProps): React.ReactElement => {
  const {t} = useTranslation('common');
  const [showLoader, setShowLoader] = useState(noDelay || false);
  const ref: React.Ref<HTMLDivElement> = createRef();

  useMountEffect(() => {
    const timerId = setTimeout(setShowLoader, 500, true);
    return (): void => window.clearTimeout(timerId);
  });

  return (
    <StyledLoading ref={ref} className={className} title={t('loading')}>
      {showLoader && (
        <object
          data={loading}
          // Height matches the Loading icon's height. It is necessary here
          // to avoid reflow after the image finished loading.
          height={80}
          tabIndex={-1}
          type='image/svg+xml'
          width={80}
        >
          {t('loading')}
        </object>
      )}
    </StyledLoading>
  );
};

const StyledLoading = styled.div`
  width: 100%;
  height: 100%;
  ${mixinFlexCenter};

  object {
    max-width: 100%;
  }
`;
